import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import SuperAdminLayout from '../components/SuperAdminLayout';

// Features deferred to Phase 2 (R9 — scraping-dependent). Don't include in counts.
const PHASE2_FEATURES = [
  'violations',
  'recruitment_links',
  'backstage_scraping',
  'whatsapp_notifications',
  'websocket_live',
  'lark_bot',
  'streamer_discovery',
];

const SuperAdminTenants = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name, plan, active, created_at, features')
        .order('created_at', { ascending: false });
      if (!cancelled) {
        setTenants(data || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) => t.name?.toLowerCase().includes(q) || t.slug?.toLowerCase().includes(q)
    );
  }, [tenants, search]);

  return (
    <SuperAdminLayout
      title={t('superadmin.tenants.title', 'Tenants')}
      subtitle={t('superadmin.tenants.subtitle', 'Manage all NoiRock tenants')}
    >
      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('superadmin.tenants.search', 'Search by name or slug...')}
          className="w-full md:w-80 px-4 py-2.5 rounded-xl bg-card border border-border text-foreground placeholder-muted-foreground/60 text-base outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl skeleton" />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-hidden">
          {/* Desktop table */}
          <table className="hidden md:table w-full">
            <thead>
              <tr className="border-b border-border bg-surface-2">
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground font-semibold px-4 py-3">
                  {t('superadmin.tenants.colName', 'Name')}
                </th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground font-semibold px-4 py-3">
                  {t('superadmin.tenants.colSlug', 'Slug')}
                </th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground font-semibold px-4 py-3">
                  {t('superadmin.tenants.colPlan', 'Plan')}
                </th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground font-semibold px-4 py-3">
                  {t('superadmin.tenants.colFeatures', 'Active features')}
                </th>
                <th className="text-left text-xs uppercase tracking-widest text-muted-foreground font-semibold px-4 py-3">
                  {t('superadmin.tenants.colStatus', 'Status')}
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((tnt) => {
                const activeFeatures = Object.entries(tnt.features || {})
                  .filter(([k]) => !PHASE2_FEATURES.includes(k))
                  .filter(([, v]) => v);
                const totalFeatures = Object.keys(tnt.features || {})
                  .filter((k) => !PHASE2_FEATURES.includes(k)).length;
                return (
                  <tr key={tnt.id} className="border-b border-border last:border-b-0 hover:bg-surface-2 transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/superadmin/tenants/${tnt.id}`} className="font-medium text-foreground hover:text-accent">
                        {tnt.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-data">{tnt.slug}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                        {tnt.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums">
                      {activeFeatures.length} / {totalFeatures}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                        tnt.active
                          ? 'bg-success/10 text-success'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {tnt.active ? t('superadmin.active', 'Active') : t('superadmin.inactive', 'Inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/superadmin/tenants/${tnt.id}`} className="text-accent text-sm font-medium hover:underline">
                        {t('superadmin.tenants.manage', 'Manage')} →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border">
            {filtered.map((tnt) => {
              const activeFeatures = Object.entries(tnt.features || {}).filter(([, v]) => v);
              return (
                <Link
                  key={tnt.id}
                  to={`/superadmin/tenants/${tnt.id}`}
                  className="block px-4 py-3 hover:bg-surface-2 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">{tnt.name}</div>
                      <div className="text-xs text-muted-foreground font-data truncate">{tnt.slug}</div>
                    </div>
                    <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                      tnt.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                    }`}>
                      {tnt.active ? t('superadmin.active', 'Active') : t('superadmin.inactive', 'Inactive')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{tnt.plan}</span>
                    <span>{activeFeatures.length} features</span>
                  </div>
                </Link>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="px-4 py-12 text-center text-muted-foreground text-sm">
              {search
                ? t('superadmin.tenants.noResults', 'No tenants match your search.')
                : t('superadmin.tenants.empty', 'No tenants yet.')}
            </div>
          )}
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminTenants;
